
import { Check } from "lucide-react";

interface StepProps {
  number: number;
  title: string;
  description: string;
}

const Step = ({ number, title, description }: StepProps) => (
  <div className="flex">
    <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-edgefi-purple text-white font-bold text-xl">
      {number}
    </div>
    <div className="ml-6">
      <h3 className="text-xl font-semibold mb-2 text-gray-900">{title}</h3>
      <p className="text-gray-600">{description}</p>
      
      <div className="mt-4 space-y-2">
        {[1, 2, 3].map((item, idx) => (
          <div key={idx} className="flex items-start">
            <div className="flex-shrink-0 flex items-center justify-center w-5 h-5 mt-1 rounded-full bg-purple-100">
              <Check className="w-3 h-3 text-edgefi-purple" />
            </div>
            <span className="ml-3 text-gray-600">
              {number === 1 && idx === 0 && "Initial security awareness assessment"}
              {number === 1 && idx === 1 && "Baseline phishing simulation"}
              {number === 1 && idx === 2 && "Customized training program design"}
              
              {number === 2 && idx === 0 && "Core security awareness modules"}
              {number === 2 && idx === 1 && "Role-specific training deployment"}
              {number === 2 && idx === 2 && "Interactive learning activities"}
              
              {number === 3 && idx === 0 && "Regular phishing simulations"}
              {number === 3 && idx === 1 && "Performance tracking and analytics"}
              {number === 3 && idx === 2 && "Continuous improvement cycles"}
              
              {number === 4 && idx === 0 && "Quarterly executive reporting"}
              {number === 4 && idx === 1 && "Security culture assessment"}
              {number === 4 && idx === 2 && "Program optimization recommendations"}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const ProcessSteps = () => {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Training Process</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We follow a structured approach to transform your employees into a strong security asset through education and practical experience.
          </p>
        </div>
        
        <div className="space-y-12 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-12 lg:gap-x-16">
          <Step 
            number={1} 
            title="Assessment & Planning" 
            description="We evaluate your current security awareness posture and develop a tailored program aligned with your specific risks and requirements."
          />
          <Step 
            number={2} 
            title="Education & Training" 
            description="Engaging training modules deliver essential security knowledge that's relevant, memorable, and applicable to everyday situations."
          />
          <Step 
            number={3} 
            title="Simulation & Practice" 
            description="Regular phishing simulations and hands-on exercises reinforce learning and build practical skills for recognizing threats."
          />
          <Step 
            number={4} 
            title="Measurement & Refinement" 
            description="Detailed analytics track progress and help continuously improve the program to address evolving threats and challenges."
          />
        </div>
      </div>
    </section>
  );
};
